import customFetch from "./helper"
import { ErrorResponse, isErrorResponse } from "@types"
import { PostMetricsResp, RevenueMetricDisplaySummary } from "@/types/metrics"
import { formatAmountAsHighestDenomString } from "@/util/formatCurrencies"

const API = "/metrics"

/**
 * Returns the total number of views for a post.
 */
export async function getPostViewsTotal(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/views/total`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the number of unique views for a post.
 */
async function getPostViewsUnique(noteId: string): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/views/unique`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the average read time for a post.
 */
export async function getPostReadTimeAverage(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/read-times/average`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the total read time for a post.
 */
export async function getPostReadTimeTotal(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/read-times/total`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the total number of views and newsletter opens for a post.
 */
export async function getPostAndNewsletterViewsTotal(
  noteId: string
): Promise<number | undefined> {
  const [totalPostViews, totalNewsletterOpens] = await Promise.all([
    getPostViewsTotal(noteId),
    getNewsletterOpensTotal(noteId),
  ])

  return (totalPostViews ?? 0) + (totalNewsletterOpens ?? 0)
}

/**
 * Returns the number of unique views and newsletter opens for a post.
 */
export async function getPostAndNewsletterViewsUnique(
  noteId: string
): Promise<number | undefined> {
  const [uniquePostViews, uniqueNewsletterOpens] = await Promise.all([
    getPostViewsUnique(noteId),
    getNewsletterOpensUnique(noteId),
  ])

  return (uniquePostViews ?? 0) + (uniqueNewsletterOpens ?? 0)
}

/**
 * Returns the total number of collectors for a post.
 */
export async function getPostCollectorsTotal(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/collectors/total`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the total number of newsletter opens for a post.
 */
export async function getNewsletterOpensTotal(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/opens/total`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the number of newsletter sends for a post.
 */
export async function getNewsletterSends(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/sends`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the number of unique newsletter opens for a post.
 */
export async function getNewsletterOpensUnique(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/opens/unique`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the open rate for a post.
 */
async function getNewsletterOpenRate(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/opens/rate`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the open rate for a post as a percentage.
 */
export async function getNewsletterOpenRateAsAPercentage(
  noteId: string
): Promise<number | undefined> {
  const response = await getNewsletterOpenRate(noteId)

  return response ? response * 100 : undefined
}

/**
 * Returns the delivery rate for a post.
 */
async function getNewsletterDeliveryRate(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/delivery/rate`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the delivery rate for a post as a percentage.
 */
export async function getNewsletterDeliveryRateAsAPercentage(
  noteId: string
): Promise<number | undefined> {
  const response = await getNewsletterDeliveryRate(noteId)

  return response ? response * 100 : undefined
}

/**
 * Returns the number of unique newsletter clicks for a post.
 */
export async function getNewsletterClicksUnique(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/clicks/unique`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the click rate for a post.
 */
export async function getNewsletterClickRate(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/clicks/rate`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the number of newsletter unsubscribes for a post.
 */
export async function getNewsletterUnsubscribesTotal(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/unsubscribes/total`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the unsubscribe rate for a post.
 */
export async function getNewsletterUnsubscribeRate(
  noteId: string
): Promise<number | undefined> {
  const response = await customFetch<number | ErrorResponse>(
    `${API}/post/${noteId}/newsletter/unsubscribes/rate`
  )

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the total revenue for a blog in USD.
 */
async function getTotalRevenueForBlog(): Promise<
  RevenueMetricDisplaySummary | undefined
> {
  const response = await customFetch<
    RevenueMetricDisplaySummary | ErrorResponse
  >(`${API}/revenue/blog`)

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the total revenue for a post in USD.
 */
async function getTotalRevenueForPost(
  noteId: string
): Promise<RevenueMetricDisplaySummary | undefined> {
  const response = await customFetch<
    RevenueMetricDisplaySummary | ErrorResponse
  >(`${API}/post/${noteId}/revenue`)

  if (!response || isErrorResponse(response)) {
    return undefined
  }

  return response
}

/**
 * Returns the total revenue for a post in USD, already formatted as a string with commas, decimals, etc.
 */
export async function getTotalRevenueForPostInUSD(
  noteId: string
): Promise<string | undefined> {
  const response = await getTotalRevenueForPost(noteId)

  if (!response) {
    return undefined
  }

  return formatAmountAsHighestDenomString(response.totalAmountInUSD, "USD")
}

/**
 * Returns the total revenue for a post in ETH, already formatted as a string with commas, decimals, etc.
 */
export async function getTotalRevenueForPostInETH(
  noteId: string
): Promise<string | undefined> {
  const response = await getTotalRevenueForPost(noteId)

  if (!response) {
    return undefined
  }

  return formatAmountAsHighestDenomString(
    response.currencyDetails.find((detail) => detail.currency === "ETH")
      ?.totalAmount || "0",
    "ETH"
  )
}

/**
 * Increments the view count or updates the read time for a post.
 * @param blogId - The ID of the blog.
 * @param postId - The ID of the post.
 * @param incrementPostView - Whether to increment the post view count.
 * @param incrementReadTime - The amount of read time to increment (in seconds).
 * @returns A Promise that resolves to void if successful, or undefined if an error occurred.
 */
export async function upsertPostMetric(
  blogId: string,
  postId: string,
  incrementPostView: boolean,
  incrementReadTime?: number
): Promise<void | undefined> {
  const response = await customFetch<void | ErrorResponse>(
    `${API}/post-track`,
    {
      method: "POST",

      // This NEEDS to be keepalive, so we sent the request even if the user navigates away from the page.
      keepalive: true,

      body: JSON.stringify({
        blogId,
        postId,
        incrementPostView,
        incrementReadTime,
      }),

      headers: {
        "Content-Type": "application/json",
      },
    }
  )

  if (!response || isErrorResponse(response)) {
    console.error("Error storing post metric.", response)
    return undefined
  }
}

/**
 * Fetch metrics for all posts for a given blog
 * @param blogId ID of the blog to fetch metrics for
 * @returns Post metrics or null if error
 */
export async function getPostMetrics(blogId: string) {
  try {
    const response = await customFetch<PostMetricsResp[] | ErrorResponse>(
      `${API}/posts/blog`,
      { next: { revalidate: 3600 } } // Cache for 1 hour
    )

    if (!response || isErrorResponse(response)) {
      console.error("Error fetching post metrics:", response)
      return null
    }

    return response
  } catch (error) {
    console.error("Error fetching post metrics:", error)
    return null
  }
}
